import { useState } from "react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Leaf, Shield, FileText, Database, CheckCircle, Loader2, Code } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { createConsentLog, CURRENT_CONSENT_VERSION, CONSENT_TEXT } from "../lib/api";
import { toast } from "sonner";
import { useDemoMode } from "../lib/DemoModeContext";
import { useTesterAccess } from "../lib/hooks/useTesterAccess";

interface ConsentProps {
  onNavigate?: (page: string) => void;
}

export function Consent({ onNavigate }: ConsentProps) {
  const { user, refreshProfile } = useAuth();
  const { enableDemoMode } = useDemoMode();
  const { canAccessDemoMode } = useTesterAccess();
  const [consentData, setConsentData] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentAge, setConsentAge] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allConsentsGiven = consentData && consentTerms && consentAge;

  const handleSubmit = async () => {
    if (!allConsentsGiven) {
      toast.error("Please accept all consents to continue");
      return;
    }

    if (!user) {
      toast.error("Please sign in first");
      onNavigate && onNavigate("signin");
      return;
    }

    setIsSubmitting(true);

    try {
      // Log consent in database
      await createConsentLog({
        user_id: user.id,
        consent_version: CURRENT_CONSENT_VERSION,
        consent_text: CONSENT_TEXT,
      });

      toast.success("Consent recorded. Welcome to CannaBias!");

      // Refresh the profile to get updated consented_at
      await refreshProfile();

      // Navigate to discover/dashboard
      setTimeout(() => {
        onNavigate && onNavigate("discover");
      }, 500);
    } catch (error: any) {
      console.error("Consent error:", error);
      toast.error(error.message || "Failed to record consent. Please try again.");
      setIsSubmitting(false);
    }
  };

  // 🔧 DEV: Enable demo mode to bypass authentication
  const handleDevLogin = () => {
    console.log("🔧 Activating Demo Mode");
    
    // Enable demo mode
    enableDemoMode();
    
    toast.success("Demo Mode Activated!", {
      description: "You can now explore the entire site without authentication",
      duration: 3000,
    });
    
    // Navigate to discover page after a brief delay
    setTimeout(() => {
      onNavigate && onNavigate("discover");
    }, 500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh py-12">
      <div className="w-full max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center shadow-lg">
                <Leaf className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl tracking-tight">CannaBias</span>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl mb-3">Consent & Verification</h1>
            <p className="text-lg text-muted-foreground">
              Please review and accept the following to continue. Your choices are recorded with version and time.
            </p>
          </div>

          {/* Consent Cards */}
          <div className="space-y-6">
            {/* Data Processing Consent */}
            <div className="p-6 rounded-2xl glass-card border-2 border-border hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg mb-2">Data Processing Consent</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    I consent to CannaBias processing my personal data, including opinions I post about prescribed cannabis products, as described in the Privacy Policy. My data is stored securely, pseudonymised where possible, and I can request deletion at any time under GDPR.
                  </p>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="consent-data"
                      checked={consentData}
                      onCheckedChange={(checked) => setConsentData(checked as boolean)}
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="consent-data"
                      className="text-sm cursor-pointer select-none"
                    >
                      I consent to data processing (GDPR Art. 6(1)(a); Art. 9(2)(a))
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Privacy */}
            <div className="p-6 rounded-2xl glass-card border-2 border-border hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg mb-2">Terms & Privacy Policy</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    I have read and agree to the{" "}
                    <button 
                      onClick={() => onNavigate && onNavigate("terms")}
                      className="text-primary hover:underline"
                      disabled={isSubmitting}
                    >
                      Terms of Use
                    </button>
                    {" "}and{" "}
                    <button 
                      onClick={() => onNavigate && onNavigate("privacy")}
                      className="text-primary hover:underline"
                      disabled={isSubmitting}
                    >
                      Privacy Policy
                    </button>
                    . CannaBias does not provide medical advice and does not sell cannabis.
                  </p>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="consent-terms"
                      checked={consentTerms}
                      onCheckedChange={(checked) => setConsentTerms(checked as boolean)}
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="consent-terms"
                      className="text-sm cursor-pointer select-none"
                    >
                      I agree to the Terms and Privacy Policy
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Age Verification */}
            <div className="p-6 rounded-2xl glass-card border-2 border-border hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg mb-2">Age & Legal Verification</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    I confirm I am legally permitted to view cannabis-related information in my location.
                  </p>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="consent-age"
                      checked={consentAge}
                      onCheckedChange={(checked) => setConsentAge(checked as boolean)}
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="consent-age"
                      className="text-sm cursor-pointer select-none"
                    >
                      I meet the legal age and local requirements
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-6 p-4 rounded-xl bg-accent/30 border border-primary/20">
            <p className="text-sm text-muted-foreground leading-relaxed">
              By proceeding, you acknowledge that consents are recorded with version {CURRENT_CONSENT_VERSION}. You can view your consent history and withdraw consent at any time via Account Settings.
            </p>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <Button
              onClick={handleSubmit}
              disabled={!allConsentsGiven || isSubmitting}
              className="w-full gradient-green hover:opacity-90 transition-opacity h-14"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Recording consent...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Accept and Continue
                </>
              )}
            </Button>
          </div>

          {/* Tester-only: Demo mode access */}
          {canAccessDemoMode && (
            <div className="mt-4 p-3 border border-dashed border-orange-500/50 rounded-lg bg-orange-500/5">
              <p className="text-xs text-orange-600 font-medium mb-2">🧪 Tester Access</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full border-orange-500/30 hover:bg-orange-500/10 text-orange-700"
                onClick={handleDevLogin}
                type="button"
              >
                <Code className="w-4 h-4 mr-2" />
                Enter Demo Mode
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
